import React, { useState, useEffect, useMemo } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from '@/components/layout/Sidebar';
import Header from '@/components/layout/Header';
import { useHeaderActions } from '@/app/contexts/HeaderActionsContext';
import styles from './MainLayout.module.scss';

// Route to page title mapping
const getPageTitle = (pathname: string): string => {
  // Remove trailing slash and get path segments
  const path = pathname.replace(/\/$/, '') || '/';
  const segments = path.split('/').filter(Boolean);

  // Handle root path
  if (segments.length === 0) return 'Dashboard';

  // Get the first segment (main section)
  const section = segments[0]!;

  if (section === 'benepass-transfers' && segments.length >= 2 && segments[1] === 'list') {
    return 'Benepass transfers — All rows';
  }

  if (section === 'paper-journal') {
    if (segments.length >= 2 && segments[1] === 'uploads' && segments.length >= 3) {
      return 'Paper journal — Upload rows';
    }
    return 'Paper Journal';
  }

  if (section === 'incomm-cards' && segments.length >= 2 && segments[1] === 'list') {
    return 'InComm Cards — All cardholders';
  }

  // Check for detail/edit/create pages
  const isDetail = segments.length >= 2 && segments[1] !== 'new';
  const isEdit = segments.includes('edit');
  const isCreate = segments[segments.length - 1] === 'new';

  // Titles for list pages and singular forms for detail/edit/create
  const titles: Record<string, { list: string; singular: string }> = {
    users: { list: 'Users', singular: 'User' },
    transactions: { list: 'Transactions', singular: 'Transaction' },
    rewards: { list: 'Rewards Catalog', singular: 'Reward' },
    partners: { list: 'Partner Configurations', singular: 'Partner Configuration' },
    offers: { list: 'Offers', singular: 'Offer' },
    fulfillments: { list: 'Fulfillments', singular: 'Fulfillment' },
    jobs: { list: 'Jobs', singular: 'Job' },
    'benepass-transfers': { list: 'Benepass transfers', singular: 'Transfer' },
    'incomm-cards': { list: 'InComm Cards', singular: 'InComm Cardholder' },
    'paper-journal': { list: 'Paper Journal', singular: 'Paper Journal Upload' },
    'marketing-banners': { list: 'Marketing Banners', singular: 'Marketing Banner' },
    analytics: { list: 'Analytics', singular: 'Analytics' },
    security: { list: 'User Management', singular: 'User Management' },
  };

  const titleConfig = titles[section];
  const listTitle = titleConfig?.list ?? section.charAt(0).toUpperCase() + section.slice(1);
  const singularTitle = titleConfig?.singular ?? listTitle.replace(/s$/, '');

  // Special case for security section which doesn't have a detail page at this level
  if (section === 'security') return listTitle;

  if (isCreate) return `New ${singularTitle}`;
  if (isEdit) return `Edit ${singularTitle}`;
  if (isDetail) return `${singularTitle} Details`;

  return listTitle;
};

const MainLayout: React.FC = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const location = useLocation();
  const { actions } = useHeaderActions();

  // Derive page title from current route
  const pageTitle = useMemo(() => getPageTitle(location.pathname), [location.pathname]);

  // Close sidebar when route changes on mobile
  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);

  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  return (
    <div className={styles.layout}>
      <Sidebar isOpen={isSidebarOpen} onClose={closeSidebar} />
      <div className={styles.mainContent}>
        <Header
          title={pageTitle}
          isSidebarOpen={isSidebarOpen}
          onToggleSidebar={toggleSidebar}
          actions={actions}
        />
        <main className={styles.contentArea}>
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default MainLayout;
